import React from 'react';

const HeroSection = () => {
  // 随机用户头像数组
  const userAvatars = [
    'https://randomuser.me/api/portraits/women/1.jpg',
    'https://randomuser.me/api/portraits/men/2.jpg',
    'https://randomuser.me/api/portraits/women/3.jpg',
    'https://randomuser.me/api/portraits/men/4.jpg',
  ];

  return (
    <section aria-label="Hero Section" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-blue-50/70 to-white">
      {/* 动态背景效果 */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-100 via-white to-transparent opacity-60" />
        <div className="absolute top-20 right-20 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob" />
        <div className="absolute top-40 -left-20 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          {/* Trust Badge */}
          <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-blue-50/80 backdrop-blur-sm mb-6 animate-fade-in-up">
            <span className="relative flex h-2 w-2 mr-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
            </span>
            <span className="text-xs font-medium text-blue-600">Trusted by 1M+ E-commerce & Creative Professionals</span>
          </div>
          
          {/* SEO Optimized Heading Structure */}
          <h1 className="max-w-4xl mx-auto text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-gray-900 leading-tight animate-fade-in-up animation-delay-150">
            <span className="inline-flex items-center gap-3">
              AI Background Remover
              <span className="tracking-wide inline-flex font-medium items-center px-2 py-1 text-sm bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-md transform -rotate-2 hover:rotate-0 transition-all duration-300 shadow-md">
                100% FREE
              </span>
            </span>
            <span className="block mt-2 pb-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              Perfect Results in Seconds
            </span>
          </h1>
          
          {/* SEO-rich description */}
          <p className="mt-6 max-w-2xl mx-auto text-xl text-gray-600 animate-fade-in-up animation-delay-300">
            Transform your images with our AI-powered background removal tool.
            <span className="block mt-2 font-medium">
              Perfect for e-commerce products, portraits, and professional photos. No signup required, 100% private processing on your device.
            </span>
          </p>

          {/* Feature Highlights with Schema-friendly structure */}
          <div role="list" aria-label="Key Features" className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4 max-w-4xl mx-auto animate-fade-in-up animation-delay-450">
            {[
              { text: 'AI Technology', icon: '🎯', desc: 'Professional-grade results' },
              { text: 'Instant Processing', icon: '⚡️', desc: 'Results in seconds' },
              { text: 'Enhanced Privacy', icon: '🔒', desc: 'Local processing only' },
              { text: 'Forever Free', icon: '∞', desc: 'No hidden costs' }
            ].map((item) => (
              <div key={item.text} role="listitem"
                   className="flex flex-col items-center p-4 rounded-xl bg-white/50 backdrop-blur-sm 
                            border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300">
                <span className="text-2xl mb-2" aria-hidden="true">{item.icon}</span>
                <h2 className="font-semibold text-gray-900">{item.text}</h2>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Optimized CTA Section */}
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animation-delay-600">
            <button
              onClick={() => {
                const element = document.querySelector('#bulk-removal');
                element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
              className="w-full sm:w-auto px-8 py-4 text-lg font-medium rounded-full text-white 
                         bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 
                         transform hover:-translate-y-0.5 transition-all duration-300 shadow-lg hover:shadow-xl
                         focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              aria-label="Start removing backgrounds from your images instantly - 100% free"
            >
              Remove Background Instantly
            </button>
            <a
              href="#examples"
              className="w-full sm:w-auto px-8 py-4 text-lg font-medium rounded-full text-gray-700 
                         bg-white hover:bg-gray-50 border border-gray-200 hover:border-blue-300
                         transform hover:-translate-y-0.5 transition-all duration-300 shadow-md hover:shadow-lg"
              aria-label="View professional examples of background removal results"
            >
              View Examples
            </a>
          </div>

          {/* Social Proof with Semantic Markup */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
            <div className="flex items-center">
              <div className="flex -space-x-2 mr-3">
                {userAvatars.map((avatar, i) => (
                  <img
                    key={i}
                    src={avatar}
                    alt={`Satisfied professional user ${i + 1}`}
                    className="w-8 h-8 rounded-full border-2 border-white object-cover shadow-sm"
                    loading="lazy"
                    width="32"
                    height="32"
                  />
                ))}
              </div>
              <span className="font-medium">Join 1,000,000+ satisfied professionals</span>
            </div>
            <div className="flex items-center gap-1.5" aria-label="Professional User Rating 4.9 out of 5">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
              <span className="font-medium">4.9/5 Professional Rating</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;